/*jshint esversion: 11 */

// Upload grande, paralelo e retomável (S3 multipart com URLs pré-assinadas) para vídeos de aula e materiais.
// A lógica pura fica em funções exportadas (testadas em tests/js); o componente Alpine só orquestra.

export const MAX_CONCURRENCY = 6;
const URL_BATCH = 20;
const URL_SAFETY_MS = 30000;
const MAX_RETRIES = 5;
const MAX_FILES = 2;
const PART_TIMEOUT_MS = 10 * 60 * 1000;

// ---------- Lógica pura ----------

export function partSizeOf(part, size, partSize) {
    const start = (part - 1) * partSize;
    return Math.max(0, Math.min(partSize, size - start));
}

/** Fatias [start, end) 1-indexadas: [{part, start, end}]. */
export function sliceRanges(size, partSize) {
    const ranges = [];
    for (let part = 1, start = 0; start < size; part++, start += partSize) {
        ranges.push({part, start, end: Math.min(size, start + partSize)});
    }
    return ranges;
}

/** Partes 1..partCount que ainda não têm ETag. */
export function missingParts(partCount, done) {
    const has = done instanceof Map ? (p) => done.has(p) : (p) => done.includes(p);
    const out = [];
    for (let p = 1; p <= partCount; p++) {
        if (!has(p)) {
            out.push(p);
        }
    }
    return out;
}

/** Espera exponencial (1s, 2s, 4s... até 30s) com jitter de 50% a 100%. */
export function backoffMs(attempt, rand = Math.random) {
    const base = Math.min(30000, 1000 * 2 ** attempt);
    return Math.round(base / 2 + rand() * (base / 2));
}

/** Bytes totais = partes concluídas + bytes já enviados das partes em voo. */
export function aggregateBytes(doneBytes, inflight) {
    let total = doneBytes;
    for (const loaded of inflight.values()) {
        total += loaded;
    }
    return total;
}

export function clampConcurrency(n) {
    const v = Number.parseInt(n, 10);
    return Math.min(MAX_CONCURRENCY, Math.max(1, Number.isFinite(v) ? v : 4));
}

/** Velocidade média numa janela deslizante. */
export function createSpeedMeter(windowMs = 8000) {
    let samples = [];
    return {
        add(t, bytes) {
            samples.push([t, bytes]);
            samples = samples.filter(([ts]) => t - ts <= windowMs);
        },
        speed() {
            if (samples.length < 2) {
                return 0;
            }
            const [t0, b0] = samples[0];
            const [t1, b1] = samples[samples.length - 1];
            return t1 > t0 ? Math.max(0, ((b1 - b0) / (t1 - t0)) * 1000) : 0;
        },
        reset() {
            samples = [];
        },
    };
}

export function resumeKey(scope, file) {
    return `courseUpload:${scope}:${file.name}:${file.size}:${file.lastModified}`;
}

function parseExpiry(expiresAt, now) {
    if (typeof expiresAt === 'number') {
        return expiresAt < 1e12 ? expiresAt * 1000 : expiresAt;
    }
    const ms = Date.parse(expiresAt);
    return Number.isFinite(ms) ? ms : now + 5 * 60 * 1000;
}

export function sleep(ms, signal) {
    return new Promise((resolve, reject) => {
        if (signal?.aborted) {
            return reject(cancelledError());
        }
        const timer = setTimeout(() => {
            signal?.removeEventListener('abort', onAbort);
            resolve();
        }, ms);
        const onAbort = () => {
            clearTimeout(timer);
            reject(cancelledError());
        };
        signal?.addEventListener('abort', onAbort, {once: true});
    });
}

export function cancelledError() {
    return Object.assign(new Error('Envio cancelado.'), {cancelled: true});
}

/** Pool: até `concurrency` workers puxando da lista; na primeira falha interrompe os demais e relança. */
export async function runPool(items, concurrency, worker, signal) {
    const queue = [...items];
    const inner = new AbortController();
    const onOuterAbort = () => inner.abort();
    signal?.addEventListener('abort', onOuterAbort, {once: true});
    let firstError = null;

    const run = async () => {
        while (!firstError && !inner.signal.aborted) {
            const item = queue.shift();
            if (item === undefined) {
                return;
            }
            try {
                await worker(item, inner.signal);
            } catch (e) {
                // Prefere um erro real a um "cancelado" provocado pela interrupção dos irmãos.
                if (!firstError || (firstError.cancelled && !e.cancelled)) {
                    firstError = e;
                }
                inner.abort();
            }
        }
    };

    await Promise.all(Array.from({length: Math.min(concurrency, queue.length) || 0}, run));
    signal?.removeEventListener('abort', onOuterAbort);

    if (firstError) {
        throw signal?.aborted ? cancelledError() : firstError;
    }
}

/**
 * Envia as partes pendentes em paralelo. `etags` (Map part -> etag) é preenchido e pode já trazer partes concluídas.
 * getUrls(parts) -> {urls:{"1":url}, expires_at}; putPart(url, blob, onLoaded, signal) -> Promise<etag>.
 */
export async function uploadParts(o) {
    const {
        file, size = file.size, partSize, partCount, etags, getUrls, putPart, onProgress = () => {},
        signal, concurrency = 4, maxRetries = MAX_RETRIES, batch = URL_BATCH,
        wait = sleep, now = Date.now, rand = Math.random,
    } = o;

    const pending = missingParts(partCount, etags);
    const inflight = new Map();
    const cache = new Map();
    const loading = new Map();
    let expiresAt = Infinity;

    let doneBytes = 0;
    for (const part of etags.keys()) {
        doneBytes += partSizeOf(part, size, partSize);
    }
    const emit = () => onProgress(aggregateBytes(doneBytes, inflight));
    emit();

    const ensureUrl = async (part) => {
        if (cache.size > 0 && now() >= expiresAt - URL_SAFETY_MS) {
            cache.clear();
        }
        if (cache.has(part)) {
            return cache.get(part);
        }
        if (!loading.has(part)) {
            const from = pending.indexOf(part);
            const wanted = pending.slice(from).filter((p) => !etags.has(p) && !cache.has(p) && !loading.has(p)).slice(0, batch);
            const request = (async () => {
                try {
                    const res = await getUrls(wanted);
                    expiresAt = parseExpiry(res.expires_at, now());
                    for (const p of wanted) {
                        if (res.urls?.[p]) {
                            cache.set(p, res.urls[p]);
                        }
                    }
                } finally {
                    wanted.forEach((p) => loading.delete(p));
                }
            })();
            wanted.forEach((p) => loading.set(p, request));
        }
        await loading.get(part);
        if (!cache.has(part)) {
            throw new Error('O servidor não devolveu o link de envio da parte ' + part + '.');
        }
        return cache.get(part);
    };

    const sendPart = async (part, innerSignal) => {
        const start = (part - 1) * partSize;
        const blob = file.slice(start, Math.min(size, start + partSize));
        for (let attempt = 0; ; attempt++) {
            if (innerSignal.aborted) {
                throw cancelledError();
            }
            try {
                const url = await ensureUrl(part);
                const etag = await putPart(url, blob, (loaded) => {
                    inflight.set(part, loaded);
                    emit();
                }, innerSignal);
                inflight.delete(part);
                etags.set(part, etag);
                doneBytes += blob.size;
                emit();
                return;
            } catch (e) {
                inflight.delete(part);
                emit();
                if (e.cancelled || innerSignal.aborted) {
                    throw cancelledError();
                }
                if (e.fatal || attempt >= maxRetries - 1) {
                    throw e;
                }
                if (e.status === 403 || e.status === 400) {
                    cache.delete(part);
                }
                await wait(backoffMs(attempt, rand), innerSignal);
            }
        }
    };

    await runPool(pending, concurrency, sendPart, signal);
}

/** PUT por XHR com progresso; devolve o ETag (exigido nas partes). */
export function xhrPut(url, body, {onProgress, signal, headers = {}, readEtag = true, XHR = globalThis.XMLHttpRequest} = {}) {
    return new Promise((resolve, reject) => {
        if (signal?.aborted) {
            return reject(cancelledError());
        }
        const xhr = new XHR();
        const onAbort = () => xhr.abort();
        const done = () => signal?.removeEventListener('abort', onAbort);
        signal?.addEventListener('abort', onAbort, {once: true});

        xhr.open('PUT', url);
        xhr.timeout = PART_TIMEOUT_MS;
        Object.entries(headers).forEach(([k, v]) => xhr.setRequestHeader(k, v));
        xhr.upload.onprogress = (e) => e.lengthComputable && onProgress?.(e.loaded);
        xhr.onload = () => {
            done();
            if (xhr.status >= 200 && xhr.status < 300) {
                const etag = readEtag ? xhr.getResponseHeader('ETag') : 'single';
                return etag
                    ? resolve(etag)
                    : reject(Object.assign(new Error('O armazenamento não devolveu o ETag da parte. Falta liberar o cabeçalho ETag no CORS do bucket (ExposeHeaders). Avise o suporte.'), {fatal: true}));
            }
            const fatal = xhr.status >= 400 && xhr.status < 500 && xhr.status !== 400 && xhr.status !== 403 && xhr.status !== 408 && xhr.status !== 429;
            reject(Object.assign(new Error(xhr.status === 403 ? 'O link de envio expirou.' : 'O envio foi recusado (' + xhr.status + ').'), {status: xhr.status, fatal}));
        };
        xhr.onabort = () => {
            done();
            reject(cancelledError());
        };
        xhr.onerror = () => {
            done();
            reject(Object.assign(new Error('Falha de rede durante o envio. Verifique a conexão.'), {status: 0}));
        };
        xhr.ontimeout = () => {
            done();
            reject(Object.assign(new Error('O envio demorou demais.'), {status: 0}));
        };
        xhr.send(body);
    });
}

// ---------- Formatação ----------

export function formatBytes(n) {
    if (n >= 1073741824) {
        return (n / 1073741824).toFixed(2).replace('.', ',') + ' GB';
    }
    return (n / 1048576).toFixed(1).replace('.', ',') + ' MB';
}

export function formatEta(seconds) {
    if (!Number.isFinite(seconds) || seconds <= 0) {
        return '';
    }
    if (seconds < 60) {
        return 'menos de 1 min';
    }
    const m = Math.ceil(seconds / 60);
    return m < 60 ? m + ' min' : Math.floor(m / 60) + ' h ' + (m % 60) + ' min';
}

const STATE_LABELS = {
    queued: 'Na fila',
    starting: 'Preparando o envio...',
    resuming: 'Retomando...',
    uploading: 'Enviando...',
    paused: 'Pausado',
    processing: 'Enviado. Processando o vídeo...',
    error: 'Erro no envio',
    done: 'Concluído',
};

const CONTENT_TYPES = {mp4: 'video/mp4', pdf: 'application/pdf'};

function guessType(file) {
    const ext = file.name.split('.').pop().toLowerCase();
    return file.type || CONTENT_TYPES[ext] || 'application/octet-stream';
}

function safeStorage() {
    try {
        return window.localStorage;
    } catch (e) {
        return null;
    }
}

// ---------- Componente Alpine ----------

export function courseUploader(cfg) {
    // Estado não reativo (File/AbortController não podem passar por proxy do Alpine).
    const rt = new Map();
    const store = safeStorage();
    const scope = `${cfg.kind}:${cfg.lessonId ?? cfg.courseId ?? 0}`;
    const maxFiles = cfg.multiple ? MAX_FILES : 1;

    const saveRecord = (key, data) => { try { store?.setItem(key, JSON.stringify(data)); } catch (e) { /* sem storage */ } };
    const loadRecord = (key) => { try { return JSON.parse(store?.getItem(key) ?? 'null'); } catch (e) { return null; } };
    const dropRecord = (key) => { try { store?.removeItem(key); } catch (e) { /* sem storage */ } };

    return {
        items: [],
        dragging: false,
        error: '',
        announce: '',
        guard: null,

        init() {
            this.guard = (e) => {
                if (this.busy) {
                    e.preventDefault();
                    e.returnValue = '';
                }
            };
            window.addEventListener('beforeunload', this.guard);
        },

        destroy() {
            window.removeEventListener('beforeunload', this.guard);
            rt.forEach((r) => r.ctrl?.abort());
            this.broadcast(false);
        },

        get busy() {
            return this.items.some((i) => ['starting', 'resuming', 'uploading'].includes(i.state));
        },

        get hasItems() {
            return this.items.length > 0;
        },

        broadcast(value) {
            window.dispatchEvent(new CustomEvent('lesson-uploading', {detail: value}));
        },

        pick(event) {
            const files = Array.from(event.target.files || []);
            event.target.value = '';
            this.add(files);
        },

        drop(event) {
            this.dragging = false;
            this.add(Array.from(event.dataTransfer?.files || []));
        },

        accepts(file) {
            const ext = (file.name.split('.').pop() || '').toLowerCase();
            if (!cfg.extensions.includes(ext)) {
                return `"${file.name}": tipo não permitido. Aceitos: ${cfg.extensions.join(', ')}.`;
            }
            if (cfg.kind === 'video' && file.type && file.type !== 'video/mp4') {
                return 'Envie o vídeo em formato MP4 (H.264 com áudio AAC).';
            }
            if (file.size > cfg.maxBytes) {
                return `"${file.name}" passa do limite de ${formatBytes(cfg.maxBytes)}.`;
            }
            if (file.size === 0) {
                return `"${file.name}" está vazio.`;
            }
            return null;
        },

        add(files) {
            const problems = [];
            if (!cfg.multiple) {
                if (this.busy) {
                    return;
                }
                files = files.slice(0, 1);
                this.items = this.items.filter((i) => i.state !== 'done' && i.state !== 'error' && i.state !== 'processing');
            }
            for (const file of files) {
                const problem = this.accepts(file);
                if (problem) {
                    problems.push(problem);
                    continue;
                }
                const key = resumeKey(scope, file);
                if (this.items.some((i) => i.storeKey === key && i.state !== 'done')) {
                    continue;
                }
                const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
                rt.set(id, {file, ctrl: null, intent: null, etags: new Map(), meter: createSpeedMeter(), lastUi: 0});
                this.items.push({id, storeKey: key, name: file.name, size: file.size, state: 'queued', bytes: 0, speed: 0, eta: 0, error: ''});
            }
            this.error = problems.join(' ');
            this.pump();
        },

        pump() {
            const active = this.items.filter((i) => ['starting', 'resuming', 'uploading'].includes(i.state)).length;
            let free = maxFiles - active;
            for (const item of this.items) {
                if (free <= 0) {
                    break;
                }
                if (item.state === 'queued') {
                    free--;
                    this.process(item);
                }
            }
        },

        setState(item, state, error = '') {
            item.state = state;
            item.error = error;
            this.announce = `${item.name}: ${error || STATE_LABELS[state]}`;
            this.broadcast(this.busy);
        },

        percent(item) {
            return item.size > 0 ? Math.min(100, Math.floor((item.bytes / item.size) * 100)) : 0;
        },

        label(item) {
            return item.state === 'error' ? item.error : STATE_LABELS[item.state];
        },

        detail(item) {
            if (item.state !== 'uploading') {
                return '';
            }
            const speed = item.speed > 0 ? (item.speed / 1048576).toFixed(1).replace('.', ',') + ' MB/s' : '';
            const eta = item.speed > 0 ? formatEta((item.size - item.bytes) / item.speed) : '';
            return [formatBytes(item.bytes) + ' de ' + formatBytes(item.size), speed, eta && 'faltam ' + eta].filter(Boolean).join(' · ');
        },

        setBytes(item, bytes) {
            const r = rt.get(item.id);
            const t = Date.now();
            r.meter.add(t, bytes);
            if (t - r.lastUi < 200 && bytes < item.size) {
                return;
            }
            r.lastUi = t;
            item.bytes = bytes;
            item.speed = r.meter.speed();
        },

        async api(url, body, signal) {
            let response;
            try {
                response = await fetch(url, {
                    method: 'POST',
                    credentials: 'same-origin',
                    signal,
                    headers: {'Content-Type': 'application/json', 'Accept': 'application/json', 'X-CSRF-TOKEN': cfg.csrf},
                    body: JSON.stringify(body),
                });
            } catch (e) {
                if (signal?.aborted) {
                    throw cancelledError();
                }
                throw Object.assign(new Error('Falha de rede. Verifique a conexão e tente de novo.'), {status: 0});
            }
            const data = await response.json().catch(() => ({}));
            if (!response.ok) {
                throw Object.assign(new Error(data.message || 'Não foi possível concluir a operação (' + response.status + ').'), {status: response.status, fatal: response.status >= 400 && response.status < 500 && response.status !== 429});
            }
            return data;
        },

        async apiRetry(url, body, signal) {
            for (let attempt = 0; ; attempt++) {
                try {
                    return await this.api(url, body, signal);
                } catch (e) {
                    if (e.cancelled || e.fatal || attempt >= 3) {
                        throw e;
                    }
                    await sleep(backoffMs(attempt), signal);
                }
            }
        },

        async process(item) {
            const r = rt.get(item.id);
            r.ctrl = new AbortController();
            r.intent = null;
            const signal = r.ctrl.signal;
            const hadId = Boolean(r.uploadId);
            this.setState(item, hadId || loadRecord(item.storeKey) ? 'resuming' : 'starting');

            try {
                // Single não tem retomada: descarta o envio anterior e começa de novo.
                if (r.uploadId && r.mode === 'single') {
                    await this.api(cfg.urls.abort, {id: r.uploadId}).catch(() => {});
                    r.uploadId = null;
                }

                if (!r.uploadId) {
                    const stored = loadRecord(item.storeKey);
                    if (stored?.id && stored.name === r.file.name && stored.size === r.file.size && stored.lastModified === r.file.lastModified) {
                        try {
                            const res = await this.apiRetry(cfg.urls.resume, {id: stored.id}, signal);
                            r.uploadId = stored.id;
                            r.mode = 'multipart';
                            r.partSize = res.part_size;
                            r.partCount = res.part_count;
                            r.concurrency = clampConcurrency(stored.concurrency);
                            (res.uploaded || []).forEach((p) => r.etags.set(p.part, p.etag));
                        } catch (e) {
                            // Falha transitória (rede/5xx/429): mantém o registro para tentar de novo; só 404/422 descarta.
                            if (e.cancelled || !(e.status === 404 || e.status === 422)) {
                                throw e;
                            }
                            dropRecord(item.storeKey);
                        }
                    }
                }

                if (!r.uploadId) {
                    const init = await this.api(cfg.urls.init, {
                        kind: cfg.kind,
                        lesson_id: cfg.lessonId ?? undefined,
                        course_id: cfg.courseId ?? undefined,
                        title: r.file.name.replace(/\.[^.]+$/, ''),
                        file_name: r.file.name,
                        size: r.file.size,
                        content_type: guessType(r.file),
                    }, signal);
                    r.uploadId = init.id;
                    r.mode = init.mode;
                    r.partSize = init.part_size;
                    r.partCount = init.part_count;
                    r.concurrency = clampConcurrency(init.concurrency);
                    r.singleUrl = init.url;
                    // O servidor decide os cabeçalhos assinados do PUT único (ex.: Content-Type do material).
                    r.singleHeaders = init.headers || {};
                    r.etags.clear();
                    if (init.mode === 'multipart') {
                        saveRecord(item.storeKey, {id: init.id, name: r.file.name, size: r.file.size, lastModified: r.file.lastModified, concurrency: r.concurrency});
                    }
                }

                this.setState(item, 'uploading');
                r.meter.reset();
                const onBytes = (bytes) => this.setBytes(item, bytes);

                if (r.mode === 'single') {
                    await xhrPut(r.singleUrl, r.file, {onProgress: onBytes, signal, readEtag: false, headers: r.singleHeaders || {'Content-Type': guessType(r.file)}});
                } else {
                    await uploadParts({
                        file: r.file,
                        partSize: r.partSize,
                        partCount: r.partCount,
                        etags: r.etags,
                        concurrency: r.concurrency,
                        signal,
                        onProgress: onBytes,
                        getUrls: (parts) => this.apiRetry(cfg.urls.parts, {id: r.uploadId, parts}, signal),
                        putPart: (url, blob, onLoaded, sig) => xhrPut(url, blob, {onProgress: onLoaded, signal: sig}),
                    });
                }

                item.bytes = item.size;
                const parts = [...r.etags.entries()].sort((a, b) => a[0] - b[0]).map(([part, etag]) => ({part, etag}));
                const result = await this.apiRetry(cfg.urls.complete, {id: r.uploadId, parts: r.mode === 'single' ? [] : parts}, signal);

                dropRecord(item.storeKey);
                this.setState(item, result.status === 'processing' ? 'processing' : 'done');
                this.finished(item);
            } catch (e) {
                if (e.cancelled) {
                    if (r.intent === 'pause') {
                        this.setState(item, 'paused');
                    }
                } else {
                    this.setState(item, 'error', e.message || 'Não foi possível enviar o arquivo.');
                    this.refresh();
                }
            } finally {
                r.ctrl = null;
                this.pump();
            }
        },

        finished(item) {
            this.refresh();
            if (item.state === 'done' && cfg.multiple) {
                setTimeout(() => { this.items = this.items.filter((i) => i.id !== item.id); }, 6000);
            }
        },

        refresh() {
            if (cfg.refresh === 'lesson') {
                this.$wire.videoSent();
            } else {
                this.$wire.dispatch('materials-uploaded');
            }
        },

        pause(item) {
            const r = rt.get(item.id);
            r.intent = 'pause';
            r.ctrl?.abort();
        },

        // Esquece o estado em memória do multipart: o `resume` do servidor traz as ETags reais (ou cai no init se o envio sumiu).
        resync(item) {
            const r = rt.get(item.id);
            if (r && r.mode === 'multipart' && loadRecord(item.storeKey)) {
                r.uploadId = null;
                r.etags.clear();
            }
        },

        resume(item) {
            this.resync(item);
            this.setState(item, 'queued');
            this.pump();
        },

        retry(item) {
            this.resync(item);
            this.setState(item, 'queued');
            this.pump();
        },

        async cancel(item) {
            const r = rt.get(item.id);
            r.intent = 'cancel';
            r.ctrl?.abort();
            this.items = this.items.filter((i) => i.id !== item.id);
            dropRecord(item.storeKey);
            rt.delete(item.id);
            this.broadcast(this.busy);
            if (r.uploadId) {
                await this.api(cfg.urls.abort, {id: r.uploadId}).catch(() => {});
            }
            this.refresh();
            this.pump();
        },
    };
}

if (typeof document !== 'undefined') {
    document.addEventListener('alpine:init', () => {
        window.Alpine.data('courseUploader', courseUploader);
    });
}
